/**
 * apptChanges — which appointments' details to re-fetch when the board document says they changed.
 *
 * WHY THIS EXISTS (2026-10-30, plan §4b). The board card shows the appointment's note and procedures from
 * the API (`get-practice-pulse-ops`), not from the board document: the exe's copy is only a first paint. To
 * keep the card live without any exe change, teamio-qprocessor writes ONE map key per changed appointment —
 * `apptChanged.<AptNum> = <time of the change>` — whenever a watched column (Note, ProcDescript, ProvNum, Op,
 * AptDateTime) of today's appointment changes in Open Dental. A map key never touches `ops`, so it cannot
 * race a drag, and no patient data goes into Firestore. The page compares the previous and the new
 * `apptChanged` map on every snapshot and re-fetches ONLY the AptNums that came back from this function.
 *
 * changedAptNums(prev, next):
 *   • `prev` null / undefined (the FIRST snapshot) ⇒ []. The page already does its one full load; refetching
 *     every card because the doc happened to carry old signals would double it.
 *   • a key that is new in `next`, or whose value differs from `prev`, is changed. Same value ⇒ not changed.
 *   • a key that disappeared (the nightly reset clears the map) is NOT changed — nothing to refetch.
 *   • keys that are not positive integers (a stray field, `__proto__`, "") are ignored.
 *   • values are compared by INSTANT: epoch milliseconds, a Firestore Timestamp ({toDate}, {seconds,
 *     nanoseconds} or {_seconds,_nanoseconds}), a Date or an ISO string all compare equal when they are the
 *     same moment, so a doc that round-trips ms → Timestamp does not look like a change. A value that cannot
 *     be read as an instant is compared as its JSON text.
 *   • missing / null values in `next` are skipped (a half-written map is not a change).
 *
 * @returns {number[]} AptNums, ascending.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

const MAX_SAFE = Number.MAX_SAFE_INTEGER;

/** A comparable token: `t:<ms>` for anything readable as an instant, else `j:<json>`. null ⇒ no value. */
function stamp(value) {
  if (value === null || value === undefined) return null;
  let ms = null;

  if (typeof value === 'number') {
    ms = Number.isFinite(value) ? value : null;
  } else if (value instanceof Date) {
    ms = value.getTime();
  } else if (typeof value === 'string') {
    const parsed = value.trim() === '' ? NaN : Date.parse(value);
    ms = Number.isFinite(parsed) ? parsed : null;
  } else if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      try {
        const d = value.toDate();
        ms = d instanceof Date ? d.getTime() : null;
      } catch (e) {
        ms = null;
      }
    } else {
      const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
      if (typeof seconds === 'number' && Number.isFinite(seconds)) {
        const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds
          : (typeof value._nanoseconds === 'number' ? value._nanoseconds : 0);
        ms = seconds * 1000 + Math.floor(nanos / 1e6);
      }
    }
  }

  if (ms !== null && Number.isFinite(ms)) return `t:${ms}`;
  try {
    return `j:${JSON.stringify(value)}`;
  } catch (e) {
    return `j:${String(value)}`;
  }
}

function isMap(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** @returns {number[]} the AptNums (positive integers) whose value is new or different in `next`. */
export function changedAptNums(prevMap, nextMap) {
  if (prevMap === null || prevMap === undefined) return [];
  if (!isMap(nextMap)) return [];
  const prev = isMap(prevMap) ? prevMap : {};

  const out = [];
  Object.keys(nextMap).forEach((key) => {
    if (!/^\d+$/.test(key)) return;
    const aptNum = Number(key);
    if (!Number.isSafeInteger(aptNum) || aptNum <= 0 || aptNum > MAX_SAFE) return;

    const after = stamp(nextMap[key]);
    if (after === null) return;
    const before = Object.prototype.hasOwnProperty.call(prev, key) ? stamp(prev[key]) : null;
    if (before !== after) out.push(aptNum);
  });
  return out.sort((a, b) => a - b);
}
